/**
 * MissionAnalysisModal — Phase 2
 * Affiche l'analyse complète d'un trajet : score chauffeur, alertes de vitesse,
 * retard à la prise en charge, temps d'attente, métriques de trajet et cohérence GPS.
 * Données depuis GET /api/admin/missions/{id}/analytics
 */
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import {
  Gauge, AlertTriangle, Clock, MapPin, Activity, Route, ShieldCheck,
  ShieldAlert, RefreshCw, TrendingUp, Octagon, Timer,
} from 'lucide-react';
import config from 'config';

const API = `${config.API_URL}/api`;

const fmtDuration = (s) => {
  if (s == null) return '—';
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = Math.round(s % 60);
  if (h > 0) return `${h}h${String(m).padStart(2, '0')}`;
  if (m > 0) return `${m} min ${String(sec).padStart(2, '0')}s`;
  return `${sec}s`;
};

const Stat = ({ icon: Icon, label, value, sub, tone = 'default', testId }) => {
  const tones = {
    default: 'text-[#E5E5E5]', good: 'text-emerald-400', warn: 'text-amber-400', bad: 'text-red-400',
  };
  return (
    <div className="bg-[#0B0B0B] border border-[#C9A24D]/15 rounded-lg p-3" data-testid={testId}>
      <div className="flex items-center gap-2 text-[#E5E5E5]/40 text-xs mb-1">
        <Icon className="w-3.5 h-3.5" /> {label}
      </div>
      <div className={`text-lg font-semibold ${tones[tone]}`}>{value}</div>
      {sub && <div className="text-[#E5E5E5]/30 text-xs mt-0.5">{sub}</div>}
    </div>
  );
};

export default function MissionAnalysisModal({ missionId, missionRef, open, onClose, getAuthHeaders }) {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  const headers = useCallback(
    () => (getAuthHeaders ? getAuthHeaders() : { Authorization: `Bearer ${localStorage.getItem('access_token')}` }),
    [getAuthHeaders],
  );

  const load = useCallback(async (refresh) => {
    if (!missionId) return;
    setLoading(true);
    setError(null);
    try {
      const r = await axios.get(`${API}/admin/missions/${missionId}/analytics${refresh ? '?refresh=true' : ''}`,
        { headers: headers(), timeout: 120000 });
      setData(r.data.analytics);
    } catch (e) {
      setError(e.response?.data?.detail || 'Échec du chargement de l\'analyse');
    } finally {
      setLoading(false);
    }
  }, [missionId, headers]);

  useEffect(() => {
    if (open && missionId) load(false);
  }, [open, missionId, load]);

  const trace = data?.trace || {};
  const delay = data?.delay || {};
  const waiting = data?.waiting || {};
  const score = data?.score || {};
  const deductions = score.deductions || {};

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="bg-[#141414] border-[#C9A24D]/30 text-[#E5E5E5] max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="mission-analysis-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center justify-between gap-3">
            <span className="flex items-center gap-2">
              <Activity className="w-5 h-5 text-[#C9A24D]" />
              Analyse du trajet {missionRef ? `— ${missionRef}` : ''}
            </span>
            <Button size="sm" variant="ghost" onClick={() => load(true)} disabled={loading}
              className="text-[#C9A24D] hover:bg-[#C9A24D]/10" data-testid="analysis-refresh-btn">
              <RefreshCw className={`w-4 h-4 mr-1 ${loading ? 'animate-spin' : ''}`} /> Recalculer
            </Button>
          </DialogTitle>
        </DialogHeader>

        {loading && !data && (
          <div className="py-12 text-center text-[#E5E5E5]/50" data-testid="analysis-loading">
            <RefreshCw className="w-6 h-6 animate-spin mx-auto mb-3 text-[#C9A24D]" />
            Calcul de l'analyse via Valhalla…
          </div>
        )}
        {error && <div className="py-6 text-center text-red-400" data-testid="analysis-error">{error}</div>}

        {data && (
          <div className="space-y-5">
            {/* Score */}
            <div className="bg-gradient-to-r from-[#C9A24D]/10 to-transparent border border-[#C9A24D]/20 rounded-lg p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 text-[#E5E5E5]/60 text-sm">
                  <Gauge className="w-4 h-4 text-[#C9A24D]" /> Impact sur le score chauffeur
                </div>
                <div className="text-right">
                  <span className={`text-3xl font-bold ${score.total_deduction > 0 ? 'text-red-400' : 'text-emerald-400'}`} data-testid="analysis-score-deduction">
                    {score.total_deduction > 0 ? `−${score.total_deduction}` : '0'}
                  </span>
                  {score.driver_score_after != null && (
                    <div className="text-[#E5E5E5]/40 text-xs">score actuel : {score.driver_score_after}/100</div>
                  )}
                </div>
              </div>
              {score.total_deduction > 0 && (
                <div className="flex flex-wrap gap-2 mt-3" data-testid="analysis-deductions">
                  {deductions.speed && <span className="px-2 py-1 rounded bg-red-500/15 text-red-300 text-xs">Excès de vitesse −{deductions.speed}</span>}
                  {deductions.delay && <span className="px-2 py-1 rounded bg-red-500/15 text-red-300 text-xs">Retard prise en charge −{deductions.delay}</span>}
                  {deductions.courtesy && <span className="px-2 py-1 rounded bg-amber-500/15 text-amber-300 text-xs">Dépassement courtoisie −{deductions.courtesy}</span>}
                </div>
              )}
            </div>

            {/* Alertes vitesse */}
            <div>
              <div className="text-[#E5E5E5]/50 text-xs uppercase tracking-wide mb-2 flex items-center gap-2">
                <AlertTriangle className="w-3.5 h-3.5" /> Alertes de vitesse
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <Stat icon={TrendingUp} label="Excès 1-10 km/h" value={trace.speed_over_orange ?? 0}
                  tone={(trace.speed_over_orange ?? 0) > 0 ? 'warn' : 'good'} testId="stat-orange" />
                <Stat icon={Octagon} label="Excès > 10 km/h" value={trace.speed_over_red ?? 0}
                  tone={(trace.speed_over_red ?? 0) > 0 ? 'bad' : 'good'} testId="stat-red" />
                <Stat icon={Gauge} label="Dépassement max" value={trace.max_overspeed_kmh ? `+${trace.max_overspeed_kmh} km/h` : '—'}
                  tone={trace.max_overspeed_kmh > 10 ? 'bad' : trace.max_overspeed_kmh > 0 ? 'warn' : 'good'} testId="stat-maxover" />
                <Stat icon={Gauge} label="Vitesse max" value={trace.max_speed_kmh ? `${trace.max_speed_kmh} km/h` : '—'} testId="stat-maxspeed" />
              </div>
            </div>

            {/* Retard & attente */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className={`rounded-lg p-3 border ${delay.is_late ? 'border-red-500/30 bg-red-500/5' : 'border-emerald-500/20 bg-emerald-500/5'}`} data-testid="analysis-delay">
                <div className="flex items-center gap-2 text-xs text-[#E5E5E5]/50 mb-1">
                  <MapPin className="w-3.5 h-3.5" /> Retard à la prise en charge
                </div>
                <div className={`font-semibold ${delay.is_late ? 'text-red-400' : 'text-emerald-400'}`}>
                  {delay.is_late ? 'En retard / absent' : 'À l\'heure'}
                </div>
                <div className="text-[#E5E5E5]/40 text-xs mt-1">
                  Distance au point de prise en charge : {delay.distance_at_pickup_m != null ? `${delay.distance_at_pickup_m} m` : '—'}
                  {delay.radius_km != null && ` (rayon ${delay.radius_km} km)`}
                </div>
              </div>
              <div className="rounded-lg p-3 border border-[#C9A24D]/15 bg-[#0B0B0B]" data-testid="analysis-waiting">
                <div className="flex items-center gap-2 text-xs text-[#E5E5E5]/50 mb-1">
                  <Timer className="w-3.5 h-3.5" /> Temps d'attente
                </div>
                <div className="font-semibold text-[#E5E5E5]">{fmtDuration(waiting.wait_seconds)}</div>
                <div className="text-[#E5E5E5]/40 text-xs mt-1">
                  Courtoisie : {waiting.courtesy_minutes ?? 0} min · Facturable : {' '}
                  <span className={waiting.billable_wait_seconds > 0 ? 'text-amber-400' : 'text-emerald-400'}>
                    {fmtDuration(waiting.billable_wait_seconds)}
                  </span>
                </div>
              </div>
            </div>

            {/* Métriques trajet */}
            <div>
              <div className="text-[#E5E5E5]/50 text-xs uppercase tracking-wide mb-2 flex items-center gap-2">
                <Route className="w-3.5 h-3.5" /> Trajet
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <Stat icon={Route} label="Distance réelle" value={`${trace.real_distance_km ?? 0} km`} testId="stat-distance" />
                <Stat icon={Clock} label="Durée" value={fmtDuration(trace.duration_s)} testId="stat-duration" />
                <Stat icon={Gauge} label="Vitesse moyenne" value={`${trace.avg_speed_kmh ?? 0} km/h`}
                  sub={`en roulant ${trace.moving_avg_speed_kmh ?? 0} km/h`} testId="stat-avgspeed" />
                <Stat icon={Octagon} label="Arrêts" value={trace.stop_count ?? 0}
                  sub={`total ${fmtDuration(trace.stop_time_s)}`} testId="stat-stops" />
              </div>
            </div>

            {/* Cohérence GPS */}
            <div>
              <div className="text-[#E5E5E5]/50 text-xs uppercase tracking-wide mb-2 flex items-center gap-2">
                {trace.coherent ? <ShieldCheck className="w-3.5 h-3.5 text-emerald-400" /> : <ShieldAlert className="w-3.5 h-3.5 text-amber-400" />}
                Cohérence GPS
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <Stat icon={ShieldCheck} label="État" value={trace.coherent === false ? 'Incohérent' : trace.coherent ? 'Cohérent' : '—'}
                  tone={trace.coherent === false ? 'warn' : 'good'} testId="stat-coherent" />
                <Stat icon={MapPin} label="Écart moyen route" value={trace.gps_mean_offset_m != null ? `${trace.gps_mean_offset_m} m` : '—'} testId="stat-offset" />
                <Stat icon={Activity} label="Points matchés" value={trace.matched_pct != null ? `${trace.matched_pct}%` : '—'} testId="stat-matched" />
                <Stat icon={AlertTriangle} label="Coupures GPS" value={trace.gaps ?? 0}
                  sub={`${trace.segments ?? 0} segments`} tone={(trace.gaps ?? 0) > 0 ? 'warn' : 'good'} testId="stat-gaps" />
              </div>
            </div>

            {/* Top routes */}
            {trace.top_roads?.length > 0 && (
              <div>
                <div className="text-[#E5E5E5]/50 text-xs uppercase tracking-wide mb-2">Routes principales</div>
                <div className="flex flex-wrap gap-2" data-testid="analysis-top-roads">
                  {trace.top_roads.map((r, i) => (
                    <span key={i} className="px-2.5 py-1 rounded-full bg-[#C9A24D]/10 border border-[#C9A24D]/20 text-[#E5E5E5]/80 text-xs">
                      {r.name} · {r.km} km
                    </span>
                  ))}
                </div>
              </div>
            )}

            <div className="text-[#E5E5E5]/25 text-[10px] text-right pt-2">
              Analyse Valhalla · {data.computed_at ? new Date(data.computed_at).toLocaleString('fr-FR') : ''}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
